import { useCallback } from 'react'
import { useIsAudioNotePaused } from '@renderer/hooks/audio/use-audio-note-selectors'
import { useHasTranscript } from '@renderer/hooks/useHasTranscript'
import AudioNoteIpcRenderer from '@renderer/ipc/AudioNoteIpc.renderer'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'

/**
 * Pause/resume and end for the running recording. `trigger` names the surface
 * offering them, for analytics.
 */
export function useLiveRecordingControls(trigger: string) {
  const isPaused = useIsAudioNotePaused()
  const hasTranscript = useHasTranscript()

  const togglePause = useCallback(() => {
    if (isPaused) {
      AudioNoteIpcRenderer.resumeRecording()
    } else {
      AudioNoteIpcRenderer.pauseRecording()
    }
  }, [isPaused])

  const endRecording = useCallback(() => {
    // With a transcript, ending saves the note; an empty recording is
    // discarded outright.
    AudioNoteIpcRenderer.stopRecording(hasTranscript)
    if (!hasTranscript) {
      AudioNoteIpcRenderer.clearAudioNoteState()
    }
    AmplitudeRenderer.trackEvent('Audio Note Action', {
      action: hasTranscript ? 'save' : 'delete',
      trigger,
    })
  }, [hasTranscript, trigger])

  return { isPaused, hasTranscript, togglePause, endRecording }
}
